import React, { useEffect } from 'react'
import { Layout, Spin, notification } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { get } from 'lodash'
import { clearToken } from '../../helpers/utility'
import { store } from '../../redux/store'
import authActions from '../../redux/auth/actions'

import Topbar from '../../components/Topbar'
import AppRouter from './AppRouter'
import { AppStyle } from './App.style'
import { GENERIC_IMAGES } from '../../constants/images'
import { useGetAuthConfig, GET_AUTH_CONFIG, subscribeGetConfig } from '../../request/query/app'
import { useQueryClient } from '@tanstack/react-query'
import { useHistory, useRouteMatch } from 'react-router-dom'

const { Content, Footer } = Layout

const logo = (() => {
  if (window.location.hostname.includes('ppibroker')) return GENERIC_IMAGES.PPBROKER_LOGO
  return GENERIC_IMAGES.PASARPOLIS_LOGO
})()

const App = () => {
  const history = useHistory()
  const queryClient = useQueryClient()
  const { data, isLoading, isSuccess, isError, error } = useGetAuthConfig()

  // Register refresh invalidator for saga-triggered refetches
  useEffect(() => {
    return subscribeGetConfig(() => queryClient.invalidateQueries([GET_AUTH_CONFIG]))
  }, [queryClient])

  // localStorage side-effect (mirror old saga behavior)
  useEffect(() => {
    if (!data) return
    const { user_permissions: userPermissions, provider_details: providerDetails } = data
    localStorage.setItem('permissions', JSON.stringify(userPermissions || []))
    const configArr = providerDetails ? providerDetails.config : null
    if (configArr) localStorage.setItem('config', JSON.stringify(configArr))
  }, [data])

  // Reset-password redirect on error with reset_request_id
  useEffect(() => {
    if (!isError || !error) return
    const resetRequestID = get(error, 'data.reset_request_id')
    const userEmail = get(error, 'data.user_email')
    const errorMessage = get(error, 'data.message')
    if (resetRequestID) {
      // Clear auth locally (mirrors what logout saga does)
      localStorage.removeItem('id_token')
      notification['error']({ message: errorMessage || 'An error occurred' })
      history.push(`/reset-password/${resetRequestID}?email=${userEmail}`)
    }
  }, [isError, error, history])

  // Invalid/expired token → clear session and go back to signin
  useEffect(() => {
    if (!isError || !error) return
    if (get(error, 'data.reset_request_id')) return  // handled by reset-password flow above
    if (get(error, 'status') !== 401) return
    clearToken()
    // Redux still holds the old token, which keeps Route isLoggedIn true and
    // bounces /signin back to /dashboard; reset it the way logout does
    store.dispatch({ type: authActions.LOGOUT_SUCCESS })
    notification['error']({ message: get(error, 'data.detail') || 'Session expired' })
    history.replace('/signin')
  }, [isError, error, history])

  const config = isSuccess && data ? (data.provider_details?.config ?? []) : []
  const userPermissions = isSuccess && data ? data.user_permissions ?? [] : []
  const userDetails = isSuccess && data ? data.user_details ?? {} : {}

  const url = useRouteMatch('/').url  // matched URL of App's own route; always '/'

  return (
    <Layout>
      <Spin spinning={!!isLoading} indicator={<LoadingOutlined type="loading" spin />} style={{ minHeight: '100vh' }}>
        {!isLoading && (
          <AppStyle>
            <Topbar
              url={url}
              config={config}
              permissions={userPermissions}
              history={history}
            />
            <Layout className='layout'>
              <Layout>
                <Content>
                  <AppRouter
                    permissions={userPermissions}
                    config={config}
                    url={url}
                    group={userDetails.groups}
                  />
                </Content>
                <div>
                  <Footer className='footer'>
                    <p>Powered by</p>
                    <img
                      width='100px'
                      height='22px'
                      className='footer-image'
                      src={logo}
                      alt="Pasarpolis Logo"
                    />
                  </Footer>
                </div>
              </Layout>
            </Layout>
          </AppStyle>
        )}
      </Spin>
    </Layout>
  )
}

App.defaultProps = {}

export default App
